import { SAGA_ACTIONS } from "../../constants";
import { invalidateVendors } from "../../vendorCache";
import { useApiCall } from "../common/appApiCallHook";

/**
 * Data access for the 1099 & vendor AP module. Components never call axios
 * directly -- every request goes through a saga action declared here.
 */

/**
 * Wraps an onSuccess so the cached vendor list is dropped before the caller
 * runs. Invalidation lives here rather than at the call sites because there
 * are seven of those across six screens, and a missed one would leave a
 * compliance screen showing stale W-9 state.
 */
const invalidatingVendors = (data: any, onSuccess: Function) => (...args: any[]) => {
    invalidateVendors(data?.branch_id ?? null);
    return onSuccess?.(...args);
};

export function useTax1099Api() {

    const callApi = useApiCall();

    const fetchVendorList = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.VENDOR_LIST, data, onSuccess, onError);
    }
    const fetchVendorDetails = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.VENDOR_DETAILS, data, onSuccess, onError);
    }
    const createVendor = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.VENDOR_CREATE, data, invalidatingVendors(data, onSuccess), onError);
    }
    const updateVendor = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.VENDOR_UPDATE, data, invalidatingVendors(data, onSuccess), onError);
    }
    const deleteVendor = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.VENDOR_DELETE, data, invalidatingVendors(data, onSuccess), onError);
    }
    const fetchPayerSettings = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYER_SETTINGS_GET, data, onSuccess, onError);
    }
    const savePayerSettings = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYER_SETTINGS_SAVE, data, onSuccess, onError);
    }
    const uploadPayerW9 = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYER_W9_UPLOAD, data, onSuccess, onError);
    }
    const downloadPayerW9Pdf = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYER_W9_PDF, data, onSuccess, onError);
    }
    const fetchPaymentList = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYMENT_LIST, data, onSuccess, onError);
    }
    const fetchPaymentDetails = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYMENT_DETAILS, data, onSuccess, onError);
    }
    const createPayment = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYMENT_CREATE, data, invalidatingVendors(data, onSuccess), onError);
    }
    const updatePayment = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYMENT_UPDATE, data, invalidatingVendors(data, onSuccess), onError);
    }
    const deletePayment = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYMENT_DELETE, data, invalidatingVendors(data, onSuccess), onError);
    }
    const fetchPaymentSummary = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYMENT_SUMMARY, data, onSuccess, onError);
    }
    const fetchFormList = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_LIST, data, onSuccess, onError);
    }
    const fetchFormDetails = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_DETAILS, data, onSuccess, onError);
    }
    const generateFormsBatch = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_GENERATE_BATCH, data, onSuccess, onError);
    }
    const generateFormForVendor = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_GENERATE_VENDOR, data, onSuccess, onError);
    }
    const extractW9 = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.W9_EXTRACT, data, onSuccess, onError);
    }
    const confirmW9 = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.W9_CONFIRM, data, invalidatingVendors(data, onSuccess), onError);
    }
    const fetchCurrentW9 = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.W9_CURRENT, data, onSuccess, onError);
    }
    const downloadW9Pdf = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.W9_PDF, data, onSuccess, onError);
    }
    const downloadFormPdf = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_PDF, data, onSuccess, onError);
    }
    const openStatementEvidence = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.STATEMENT_EVIDENCE, data, onSuccess, onError);
    }
    const approveForm = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_APPROVE, data, onSuccess, onError);
    }
    const unapproveForm = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_UNAPPROVE, data, onSuccess, onError);
    }
    const voidForm = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_VOID, data, onSuccess, onError);
    }
    const furnishFormByEmail = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_FURNISH_EMAIL, data, onSuccess, onError);
    }
    const correctForm = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.FORM_CORRECT, data, onSuccess, onError);
    }
    const syncQboVendors = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.VENDOR_SYNC_QBO, data, invalidatingVendors(data, onSuccess), onError);
    }
    const syncExpenses = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.PAYMENT_SYNC_EXPENSES, data, invalidatingVendors(data, onSuccess), onError);
    }
    const fetchVendorPayments = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.VENDOR_PAYMENTS, data, onSuccess, onError);
    }
    const fetchNextVendorCode = (data: any, onSuccess: Function, onError?: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.VENDOR_NEXT_CODE, data, onSuccess, onError || (() => {}));
    }
    const fetchYearEndPackageStatus = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.YEAR_END_STATUS, data, onSuccess, onError);
    }
    const approveYearEndPackage = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.YEAR_END_APPROVE, data, onSuccess, onError);
    }
    const fetchUnmappedPayees = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.UNMAPPED_PAYEES, data, onSuccess, onError);
    }
    const resolvePayee = (data: any, onSuccess: Function, onError: Function) => {
        callApi(SAGA_ACTIONS.TAX_1099.RESOLVE_PAYEE, data, onSuccess, onError);
    }

    return {
        fetchUnmappedPayees,
        resolvePayee,
        fetchVendorList,
        fetchVendorDetails,
        createVendor,
        updateVendor,
        deleteVendor,
        fetchPayerSettings,
        savePayerSettings,
        uploadPayerW9,
        downloadPayerW9Pdf,
        fetchPaymentList,
        fetchPaymentDetails,
        createPayment,
        updatePayment,
        deletePayment,
        fetchPaymentSummary,
        fetchFormList,
        fetchFormDetails,
        generateFormsBatch,
        generateFormForVendor,
        extractW9,
        confirmW9,
        fetchCurrentW9,
        downloadW9Pdf,
        downloadFormPdf,
        openStatementEvidence,
        approveForm,
        unapproveForm,
        voidForm,
        furnishFormByEmail,
        correctForm,
        syncQboVendors,
        syncExpenses,
        fetchVendorPayments,
        fetchNextVendorCode,
        fetchYearEndPackageStatus,
        approveYearEndPackage,
    }
}
